import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiSearch, FiFilter, FiStar, FiShoppingCart, FiHeart } from 'react-icons/fi';
import API_BASE_URL from '../config';

const CatalogContainer = styled.div`
  min-height: 100vh;
  background-color: #f8fafc;
  padding: 40px 0;
`;

const CatalogContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const CatalogHeader = styled.div`
  margin-bottom: 40px;
`;

const CatalogTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 16px;
`;

const CatalogSubtitle = styled.p`
  font-size: 1.125rem;
  color: #6b7280;
  margin-bottom: 32px;
`;

const SearchAndFilters = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 40px;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const SearchContainer = styled.div`
  flex: 1;
  position: relative;
  min-width: 300px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 16px 16px 16px 48px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 16px;
  background-color: white;
  
  &:focus {
    outline: none;
    border-color: #2563eb;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
  }
  
  &::placeholder {
    color: #9ca3af;
  }
`;

const SearchIcon = styled(FiSearch)`
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #6b7280;
  font-size: 20px;
`;

const FilterContainer = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
`;

const FilterSelect = styled.select`
  padding: 16px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 16px;
  background-color: white;
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: #2563eb;
  }
`;

const FilterButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px 24px;
  background-color: white;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 500;
  color: #374151;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: #2563eb;
    color: #2563eb;
  }
`;

const BooksGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 24px;
  margin-bottom: 40px;
`;

const BookCard = styled(motion.div)`
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  }
`;

const BookImage = styled.div`
  width: 100%;
  height: 200px;
  background: linear-gradient(135deg, #667eea, #764ba2);
  position: relative;
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const BookOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
  
  ${BookCard}:hover & {
    opacity: 1;
  }
`;

const QuickActions = styled.div`
  display: flex;
  gap: 12px;
`;

const QuickButton = styled.button`
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #374151;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: white;
    transform: scale(1.1);
  }
`;

const BookInfo = styled.div`
  padding: 20px;
`;

const BookTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 8px;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const BookAuthor = styled.p`
  color: #6b7280;
  font-size: 0.875rem;
  margin-bottom: 12px;
`;

const BookRating = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
`;

const Stars = styled.div`
  display: flex;
  gap: 2px;
  color: #fbbf24;
`;

const RatingText = styled.span`
  color: #6b7280;
  font-size: 0.875rem;
`;

const BookPrice = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const Price = styled.span`
  font-size: 1.25rem;
  font-weight: 700;
  color: #1f2937;
`;

const OriginalPrice = styled.span`
  font-size: 1rem;
  color: #9ca3af;
  text-decoration: line-through;
  margin-left: 8px;
`;

const BookActions = styled.div`
  display: flex;
  gap: 12px;
`;

const AddToCartButton = styled.button`
  flex: 1;
  padding: 12px 16px;
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const WishlistButton = styled.button`
  width: 44px;
  height: 44px;
  background: #f3f4f6;
  border: none;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #e5e7eb;
    color: #ef4444;
  }
`;

const LoadMoreButton = styled.button`
  display: block;
  margin: 40px auto 0;
  padding: 16px 32px;
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-weight: 600;
  color: #374151;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: #2563eb;
    color: #2563eb;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 80px 20px;
  color: #6b7280;
  
  h3 {
    font-size: 1.5rem;
    margin-bottom: 16px;
    color: #374151;
  }
  
  p {
    font-size: 1rem;
    margin-bottom: 24px;
  }
`;

const BookCatalog = ({ onAddToCart }) => {
  const [books, setBooks] = useState([]);
  const [filteredBooks, setFilteredBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [sortBy, setSortBy] = useState('newest');

  useEffect(() => {
    // Fetch books from API
    const fetchBooks = async () => {
      try {
        setLoading(true);
        const params = new URLSearchParams();
        if (searchQuery) params.append('search', searchQuery);
        if (selectedCategory) params.append('category', selectedCategory);
        if (sortBy) params.append('sortBy', sortBy);
        
        const url = `${API_BASE_URL}/books.php${params.toString() ? '?' + params.toString() : ''}`;
        console.log('Fetching books from:', url);
        
        const response = await fetch(url);
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        console.log('Books API response:', data);
        
        if (data.success && data.books && Array.isArray(data.books)) {
          // Map database fields to component fields
          const mappedBooks = data.books.map(book => ({
            id: book.id,
            title: book.title,
            author: book.author,
            price: parseFloat(book.price),
            originalPrice: book.original_price ? parseFloat(book.original_price) : null,
            image: book.image || 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?w=300&h=400&fit=crop',
            category: book.category || 'Fiction',
            rating: parseFloat(book.rating) || 0,
            reviews: parseInt(book.reviews) || 0,
            description: book.description || ''
          }));
          
          setBooks(mappedBooks);
          setFilteredBooks(mappedBooks);
        } else {
          console.warn('API returned no books or invalid format:', data);
          // Return empty array if no books found (table might be empty)
          setBooks([]);
          setFilteredBooks([]);
        }
      } catch (error) {
        console.error('Error fetching books from API:', error);
        // If API fails, show empty state instead of crashing
        setBooks([]);
        setFilteredBooks([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchBooks();
  }, [searchQuery, selectedCategory, sortBy]);

  // Filtering and sorting is now handled by the API
  // This effect just updates filteredBooks when books change
  useEffect(() => {
    setFilteredBooks(books);
  }, [books]);

  const categories = ['', ...new Set(books.map(book => book.category))];

  if (loading) {
    return (
      <CatalogContainer>
        <CatalogContent>
          <div style={{ textAlign: 'center', padding: '80px 0' }}>
            <h2>Loading books...</h2>
          </div>
        </CatalogContent>
      </CatalogContainer>
    );
  }

  return (
    <CatalogContainer>
      <CatalogContent>
        <CatalogHeader>
          <CatalogTitle>Browse Books</CatalogTitle>
          <CatalogSubtitle>Discover your next favorite read from our collection</CatalogSubtitle>
        </CatalogHeader>

        <SearchAndFilters>
          {/* <SearchContainer>
            <SearchIcon />
            <SearchInput
              type="text"
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </SearchContainer> */}

          <FilterContainer>
            <FilterSelect
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              <option value="">All Categories</option>
              {categories.slice(1).map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </FilterSelect>

            <FilterSelect
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="newest">Newest First</option>
              <option value="price-low">Price: Low to High</option>
              <option value="price-high">Price: High to Low</option>
              <option value="rating">Highest Rated</option>
            </FilterSelect>

            <FilterButton>
              <FiFilter />
              More Filters
            </FilterButton>
          </FilterContainer>
        </SearchAndFilters>

        {filteredBooks.length === 0 ? (
          <EmptyState>
            <h3>No books found</h3>
            <p>Try adjusting your search or filter criteria</p>
          </EmptyState>
        ) : (
          <>
            <BooksGrid>
              {filteredBooks.map((book, index) => (
                <BookCard
                  key={book.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <BookImage>
                    <img src={book.image} alt={book.title} />
                    <BookOverlay>
                      <QuickActions>
                        <QuickButton>
                          <FiHeart />
                        </QuickButton>
                        <QuickButton>
                          <FiShoppingCart />
                        </QuickButton>
                      </QuickActions>
                    </BookOverlay>
                  </BookImage>

                  <BookInfo>
                    <BookTitle>{book.title}</BookTitle>
                    <BookAuthor>by {book.author}</BookAuthor>

                    <BookRating>
                      <Stars>
                        {[...Array(5)].map((_, i) => (
                          <FiStar
                            key={i}
                            size={14}
                            fill={i < Math.floor(book.rating) ? '#fbbf24' : 'none'}
                          />
                        ))}
                      </Stars>
                      <RatingText>({book.reviews})</RatingText>
                    </BookRating>

                    <BookPrice>
                      <Price>${book.price}</Price>
                      {book.originalPrice && (
                        <OriginalPrice>${book.originalPrice}</OriginalPrice>
                      )}
                    </BookPrice>

                    <BookActions>
                      <AddToCartButton onClick={() => onAddToCart(book)}>
                        Add to Cart
                      </AddToCartButton>
                      <WishlistButton>
                        <FiHeart />
                      </WishlistButton>
                    </BookActions>
                  </BookInfo>
                </BookCard>
              ))}
            </BooksGrid>

            {/* <LoadMoreButton>
              Load More Books
            </LoadMoreButton> */}
          </>
        )}
      </CatalogContent>
    </CatalogContainer>
  );
};

export default BookCatalog;
